import type { ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { Icon } from '@/components/base/Icon';
import { Link } from '@/components/base/Link';
import { t } from '@/messages/zh-TW';

/**
 * 第二層版型：頂列。
 *
 * 原系統對應：`layout/navbar.jsp:79-105`
 *   `:82`      收合鈕 `<a class="navbar-minimalize ... btn btn-primary btn-outline"><i class="fa fa-bars">`
 *              onClick 呼叫 `ePortfolio.adjTableListWidth()`（重算 DataTable 寬度）
 *   `:84-88`   左側標題 `<h3>${sysTitle} ${sysEnvDesc}</h3>`
 *   `:91-97`   右側「切換身分」下拉（多重身分時才出現）→ 由 <RoleSwitcher> 負責
 *   `:97`      大頭照 `<img src="getPhoto?userId=..." width=35 height=35 class="person-image img-circle"`
 *              onclick `showChangeProfileModal()` 開「個人登入資訊」modal（`:107-181`）
 *   `:99-102`  登出 `<a href="/logout"><i class="fa fa-sign-out"></i>登出</a>`
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface NavbarProps {
  uiId: string;
  /** `${sysTitle} ${sysEnvDesc}`（navbar.jsp:86）。 */
  title: string;
  /** 大頭照網址。原系統是 `getPhoto?userId=...`；沒有就不畫。 */
  photoUrl?: string;
  /** 大頭照替代文字（通常是使用者姓名）。 */
  userName?: string;
  /** 收合鈕。原系統同時會呼叫 adjTableListWidth() 重算表格寬度。 */
  onToggleSidebar?(): void;
  /** 點大頭照 → 個人登入資訊 modal。 */
  onProfileClick?(): void;
  /** 登出連結的 href。 */
  logoutHref?: string;
  onLogout?(): void;
  /** 身分切換區，通常放 <RoleSwitcher>；沒有多重身分時不傳。 */
  roleSwitcher?: ReactNode;
  /**
   * batch 0：開發／測試用工具列（例如 <ResetDataButton>）。**原系統沒有對應**——
   * navbar.jsp 沒有這個位置，純粹是雛形展示／測試工具的掛載點，沒有東西要放時不傳。
   * 刻意跟 `roleSwitcher` 分開一個 slot，不混進去：那個 slot 的語意是「身分切換區」。
   */
  devToolbar?: ReactNode;
}

export function Navbar({
  uiId,
  title,
  photoUrl,
  userName,
  onToggleSidebar,
  onProfileClick,
  logoutHref = '#',
  onLogout,
  roleSwitcher,
  devToolbar,
}: NavbarProps) {
  return (
    <header
      data-ui-id={uiId}
      className="flex items-center gap-3 border-border border-b bg-surface px-3 py-2"
    >
      <Button
        uiId={`${uiId}-toggle`}
        variant="primary"
        size="sm"
        {...(onToggleSidebar === undefined ? {} : { onClick: onToggleSidebar })}
      >
        <Icon uiId={`${uiId}-toggle-icon`} name="bars" label={t('a11y.toggleSidebar')} />
      </Button>

      <h1 data-ui-id={`${uiId}-title`} className="truncate font-bold text-[18px]">
        {title}
      </h1>

      <div data-ui-id={`${uiId}-actions`} className="ml-auto flex items-center gap-3">
        {roleSwitcher}
        {devToolbar}
        {photoUrl === undefined ? null : (
          // 原系統是「圖片本身可點」（navbar.jsp:97 img onclick）。這裡包一層 button
          // 才有鍵盤可及性 —— 刻意的無障礙修正，不是原系統行為。
          <button
            type="button"
            data-ui-id={`${uiId}-profile`}
            onClick={onProfileClick}
            className="rounded-full"
          >
            <img
              src={photoUrl}
              alt={userName ?? ''}
              width={35}
              height={35}
              className="h-[35px] w-[35px] rounded-full object-cover"
            />
          </button>
        )}
        <Link
          uiId={`${uiId}-logout`}
          href={logoutHref}
          {...(onLogout === undefined ? {} : { onClick: onLogout })}
        >
          {t('action.logout')}
        </Link>
      </div>
    </header>
  );
}
